import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDownRight, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhY2NvdW50aW5nJTIwb2ZmaWNlJTIwZGVzayUyMGRvY3VtZW50cyUyMGNvbnN1bHRhdGlvbnxlbnwwfHx8fDE3OTA0MzExNDd8MA&ixlib=rb-4.1.0&q=80&w=1200&auto=format&fit=crop";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 90]);

  return (
    <section id="top" className="relative overflow-hidden pt-[76px]" data-testid="hero-section">
      <div className="pointer-events-none absolute -top-32 -right-40 h-[480px] w-[480px] rounded-full bg-brass-light blur-3xl opacity-70" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-brass-dark font-semibold mb-6"
            data-testid="hero-eyebrow"
          >
            Ghaziabad · Delhi NCR — Est. Trust
          </motion.p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-ink" data-testid="hero-heading">
            <MaskedLine delay={0.15}>Chartered Accountant</MaskedLine>
            <MaskedLine delay={0.3}>
              in <span className="italic text-brass-dark">Ghaziabad/NCR</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-ink-muted"
          >
            Shashank Chaudhary &amp; Co helps businesses and families across Indirapuram,
            Raj Nagar and Vaishali stay compliant and confident — GST, income tax,
            corporate law and audit, handled end to end.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a href="https://wa.me/919654852550?text=Hello%2C%20I%20need%20a%20Chartered%20Accountant%20in%20Ghaziabad."
              target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-cta"
              className="inline-flex items-center gap-2 rounded-full bg-forest text-white font-semibold px-7 py-3.5 hover:bg-ink transition-colors duration-300">
              <MessageCircle size={18} /> Chat on WhatsApp
            </a>
            <a href="#contact" data-testid="hero-contact-cta"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 text-ink font-semibold px-7 py-3.5 hover:border-brass hover:text-brass-dark transition-colors duration-300">
              Request a Callback <ArrowDownRight size={18} />
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-xs font-medium text-ink-soft"
            data-testid="hero-icai-pill"
          >
            <ShieldCheck size={14} className="text-brass-dark" />
            ICAI Membership No. 448639 · Firm Regn. No. 029777C
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)]"
          >
            <motion.img
              src={HERO_IMG}
              alt="Chartered accountant's desk with financial documents at Shashank Chaudhary & Co, Ghaziabad"
              className="h-[420px] sm:h-[520px] w-full object-cover scale-110"
              style={{ y: imgY }}
              fetchpriority="high"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex flex-wrap gap-2">
              {["GST", "Income Tax", "Corporate Law", "Audit"].map((t) => (
                <span key={t} className="rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-medium px-3.5 py-1.5">
                  {t}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
